import { useMemo, useState } from 'react';
import { Link2, Unlink, RefreshCw } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { useAnnulerLettrage, useLettrer, useLignesLettrage } from '@/hooks/useJournal';
import { useAuthStore } from '@/stores/authStore';
import { extractError } from '@/api/client';
import { formatNumber } from '@/utils/currency';
import { demander } from '@/components/ui/Fenetres';

const COMPTES = [
  { code: '411000', label: 'Clients' },
  { code: '401000', label: 'Fournisseurs' },
];
const F = (centimes: string | bigint) => formatNumber(Number(BigInt(centimes)) / 100);
const date = (iso: string) => new Date(iso).toLocaleDateString('fr-FR');

/**
 * Lettrage — lot 2, étape 2.4.
 * Rapprocher, pour un compte client / fournisseur et un tiers, des lignes dont le total débit = le total crédit
 * (une facture et ses paiements). Les factures soldées sont lettrées automatiquement ; le DAF lettre à la main le reste
 * et peut délettrer avec un motif. Le lettrage ne modifie jamais une écriture validée.
 */
export const LettragePage = () => {
  const role = useAuthStore((s) => s.user?.role);
  const peutLettrer = role === 'daf';
  const [compte, setCompte] = useState('411000');
  const [tiers, setTiers] = useState<string | undefined>(undefined);
  const [choix, setChoix] = useState<Set<string>>(new Set());
  const [erreur, setErreur] = useState<string | null>(null);
  const { data, isLoading, refetch } = useLignesLettrage(compte, tiers);
  const lettrer = useLettrer();
  const annuler = useAnnulerLettrage();

  const lignes = data?.lignes ?? [];
  const selection = useMemo(() => lignes.filter((l) => choix.has(l.id)), [lignes, choix]);
  const totD = selection.reduce((s, l) => s + BigInt(l.debitCents), 0n);
  const totC = selection.reduce((s, l) => s + BigInt(l.creditCents), 0n);
  const equilibre = selection.length >= 2 && totD === totC && totD > 0n;

  const basculer = (id: string) => setChoix((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const changerTiers = (id?: string) => { setTiers(id); setChoix(new Set()); setErreur(null); };

  const faireLettrer = async () => {
    setErreur(null);
    try { await lettrer.mutateAsync([...choix]); setChoix(new Set()); } catch (e) { setErreur(extractError(e).message); }
  };
  const delettrer = async (id: string, code: string) => {
    const motif = await demander.texte({ titre: `Délettrer ${code}`, libelle: 'Motif (obligatoire, tracé dans l’historique)', min: 5, confirmer: 'Délettrer', danger: true });
    if (motif === null) return;
    setErreur(null);
    try { await annuler.mutateAsync({ id, motif }); } catch (e) { setErreur(extractError(e).message); }
  };

  return (
    <>
      <Topbar
        title="Lettrage"
        subtitle="Rapprocher les factures et leurs paiements, par client ou fournisseur"
        actions={<button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()} aria-label="Rafraîchir"><RefreshCw size={14} /></button>}
      />
      <div className="ds-content">
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {COMPTES.map((c) => (
            <button key={c.code} type="button" className={`ds-btn ds-btn-sm ${compte === c.code ? 'ds-btn-p' : 'ds-btn-g'}`}
              onClick={() => { setCompte(c.code); changerTiers(undefined); }}>
              {c.code} · {c.label}
            </button>
          ))}
        </div>

        {erreur && <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 12 }}><div className="ds-alert-txt">{erreur}</div></div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 300px) 1fr', gap: 16, alignItems: 'start' }}>
          {/* Tiers avec un solde non lettré */}
          <div className="ds-table-panel" style={{ padding: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8, color: 'var(--tx-2)' }}>Non lettré par tiers</div>
            <button type="button" onClick={() => changerTiers(undefined)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 8px', borderRadius: 6, border: 0, cursor: 'pointer',
                       background: !tiers ? 'var(--bg)' : 'transparent', fontSize: 12 }}>
              Tous les tiers
            </button>
            {(data?.nonLettre ?? []).map((t) => (
              <button key={t.contactId} type="button" onClick={() => changerTiers(t.contactId)}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 8, width: '100%', textAlign: 'left', padding: '6px 8px',
                         borderRadius: 6, border: 0, cursor: 'pointer', background: tiers === t.contactId ? 'var(--bg)' : 'transparent', fontSize: 12 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.nom}</span>
                <span className="mono" style={{ whiteSpace: 'nowrap' }}>{F(t.solde)}</span>
              </button>
            ))}
            {!isLoading && (data?.nonLettre ?? []).length === 0 && <div style={{ fontSize: 12, color: 'var(--tx-3)' }}>Tout est lettré.</div>}
          </div>

          {/* Lignes */}
          <div className="ds-table-panel">
            <table className="ds-tbl" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th><th>Date</th><th>N° pièce</th><th>Tiers</th><th>Libellé</th>
                  <th style={{ textAlign: 'right' }}>Débit</th><th style={{ textAlign: 'right' }}>Crédit</th><th>Lettrage</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && <tr><td colSpan={8} style={{ padding: 16, color: 'var(--tx-3)' }}>Chargement…</td></tr>}
                {!isLoading && lignes.length === 0 && <tr><td colSpan={8} style={{ padding: 16, color: 'var(--tx-3)' }}>Aucune ligne validée sur ce compte.</td></tr>}
                {lignes.map((l) => {
                  const let_ = l.lettrage?.lettrage;
                  return (
                    <tr key={l.id} style={let_ ? { opacity: 0.6 } : undefined}>
                      <td>{!let_ && peutLettrer && <input type="checkbox" checked={choix.has(l.id)} onChange={() => basculer(l.id)} aria-label="Choisir la ligne" />}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{date(l.entry.entryDate)}</td>
                      <td><span className="ds-ref-code">{l.entry.numeroPiece ?? l.entry.reference}</span></td>
                      <td style={{ fontSize: 12 }}>{l.contact?.companyName ?? l.contact?.fullName ?? '—'}</td>
                      <td style={{ fontSize: 12 }}>{l.label}</td>
                      <td className="ds-am">{BigInt(l.debitCents) > 0n ? F(l.debitCents) : '—'}</td>
                      <td className="ds-am">{BigInt(l.creditCents) > 0n ? F(l.creditCents) : '—'}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                        {let_ ? (
                          <>
                            <span className="ds-ref-code">{let_.code}</span>{let_.automatique && <span style={{ color: 'var(--tx-3)' }}> auto</span>}
                            {peutLettrer && (
                              <button type="button" className="ds-row-btn" title="Délettrer (motif obligatoire)" onClick={() => void delettrer(let_.id, let_.code)}>
                                <Unlink size={12} />
                              </button>
                            )}
                          </>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {peutLettrer && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: 12, borderTop: '1px solid var(--bdr)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12 }}>
                  Sélection : {selection.length} ligne(s) · débit <b className="mono">{F(totD)}</b> · crédit <b className="mono">{F(totC)}</b>
                  {selection.length >= 2 && !equilibre && <span style={{ color: 'var(--danger)' }}> · écart {F(totD > totC ? totD - totC : totC - totD)}</span>}
                </span>
                <button type="button" className="ds-btn ds-btn-p ds-btn-sm" disabled={!equilibre || lettrer.isPending} onClick={() => void faireLettrer()}>
                  <Link2 size={14} /> Lettrer
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
